<template>
  <div
    v-if="conditionOptions.length"
    class="c-serviceSearchHeaderFacet"
  >
    <ServiceSearchHeaderFacetTabs
      class="c-serviceSearchHeaderFacet_tabs"
      :focused-tab-id="focusedTabId"
      :condition-options="conditionOptions"
      @change="onChangeFocusTab($event)"
    />
    <template v-for="(cassettes, index) in conditionOptions">
      <ServiceSearchHeaderFacetCassettes
        :key="`cassettes${cassettes.master.id}_${index}`"
        class="c-serviceSearchHeaderFacet_cassettes"
        :cassettes="cassettes"
        v-show="focusedTabId === cassettes.master.id"
        @change-cassette="clearFacetKeyAndSetAttributes($event)"
      />
    </template>
    <ServiceSearchHeaderFacetSelectedItems
      class="c-serviceSearchHeaderFacet_selectedItems"
      :condition-options="conditionOptions"
      @clear-facet="setFacetKeyAndSetAttributes($event)"
    />
  </div>
</template>
<script>
import ServiceSearchHeaderFacetTabs from './components/ServiceSearchHeaderFacetTabs'
import ServiceSearchHeaderFacetCassettes from './components/ServiceSearchHeaderFacetCassettes'
import ServiceSearchHeaderFacetSelectedItems from './components/ServiceSearchHeaderFacetSelectedItems'
import { mapActions, mapState, mapMutations } from 'vuex'

const CATEGORY_TYPE_DB_KEY = 'master_category_type_id'
const CATEGORY_TYPE_QUERY_KEY = 'category_type_id'

export default {
  name: 'ServiceSearchHeaderFacet',
  components: {
    ServiceSearchHeaderFacetTabs,
    ServiceSearchHeaderFacetCassettes,
    ServiceSearchHeaderFacetSelectedItems
  },
  props: {
    attributes: {
      type: Object,
      default: () => ({})
    },
    searchOptionMasters: {
      type: Array,
      default() {
        return []
      }
    },
    condition: {
      type: Object,
      default() {
        return {}
      }
    },
    conditionOptionsCommon: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      innerCondition: {},
      innerCategoryId: null,
      innerCategoryTypeId: null,
      focusedTabId: null,
      masterCategoryId: null,
      isCategoryLocked: false
    }
  },
  watch: {
    condition: {
      deep: true,
      handler() {
        this.innerCondition = this.$util.clone(this.condition)
      }
    },
    conditionOptions: {
      deep: true,
      handler() {
        if (this.conditionOptions.length > 0) {
          this.focusedTabId = this.getFocusedTabId()
        }
      }
    }
  },
  computed: {
    ...mapState('pages/search/facet', ['selectedFacetKey', 'selectedFacetCategoryId']),
    conditionOptions() {
      if (!this.innerCondition.conditionOptionsList) {
        return []
      }
      return this.innerCondition.conditionOptionsList
        .map(option => {
          const master = this.searchOptionMasters.find(
            e => e.keyCategoryId === option.keyCategoryId && e.searchDisplayPosition === 'top'
          )
          // マスターの存在しない検索オプションは除外する
          if (!master) {
            return null
          }
          return {
            option,
            master
          }
        })
        .filter(x => !!x)
    }
  },
  created() {
    this.innerCondition = this.$util.clone(this.condition)
    this.focusedTabId = this.getFocusedTabId()
    this.masterCategoryId = this.$route.params.categoryId || this.$route.query.category_id
    this.isCategoryLocked = !!this.$route.params.categoryId || !!this.$route.query.category_id
  },

  methods: {
    ...mapActions('pages/search/facet', ['setFacetKey']),
    ...mapMutations('pages/search/facet', ['CLEAR_FACET']),
    setAttributes(key, value, emitChangeEvent = true) {
      const newKey = key === CATEGORY_TYPE_DB_KEY ? CATEGORY_TYPE_QUERY_KEY : key
      const newValue = key === CATEGORY_TYPE_DB_KEY ? value?.[0] ?? [] : value
      this.$emit('change-attributes', {
        key: newKey,
        value: newValue,
        emitChangeEvent
      })
    },
    /**
     * カテゴリ固有の検索条件変更
     * attributesへの変更がすべて完了してからchangeイベントを発火させる
     */
    setOptionAttributes(event) {
      const { key, value, categoryId } = event
      this.setAttributes(key, value, false)
      // 関連カテゴリIDのある検索オプションのキーを抽出
      const categorizedOptionKeys = this.conditionOptionsCommon
        .filter(o => o.option.relatedCategoryId && o.master.key)
        .map(o => o.master.key)
      // minKey、maxKeyを持つ検索オプションの対応
      this.conditionOptionsCommon
        .filter(o => o.option.relatedCategoryId && (o.master.minKey || o.master.maxKey))
        .forEach(o => {
          if (o.master.minKey) {
            categorizedOptionKeys.push(o.master.minKey)
          }
          if (o.master.maxKey) {
            categorizedOptionKeys.push(o.master.maxKey)
          }
        })

      if (
        !this.isCategoryLocked &&
        categorizedOptionKeys.includes(key) &&
        this.isFalsyOrEmpty(value)
      ) {
        // 関連カテゴリIDのある検索オプションがすべて"使用されていない"ことを確認
        const hasNoCategorizedOptions = categorizedOptionKeys.every(k => {
          let camelCaseKey = k.replace(/_([a-z])/g, (_, p) => p.toUpperCase())
          if (camelCaseKey === 'masterCategoryTypeId') {
            camelCaseKey = 'categoryTypeId'
          }
          return this.isFalsyOrEmpty(this.attributes[camelCaseKey])
        })
        if (hasNoCategorizedOptions) {
          this.setAttributes('categoryId', null, false)
        }
        this.$emit('change', this.attributes)
        return
      }
      if (categoryId) {
        this.setAttributes('categoryId', categoryId, false)
      }
      this.$emit('change', this.attributes)
    },
    isFalsyOrEmpty(value) {
      if (!value) {
        return true
      }
      if (Array.isArray(value)) {
        return value.length === 0
      }
      if (typeof value === 'object') {
        return Object.keys(value).length === 0
      }
      return false
    },
    clearFacetKeyAndSetAttributes({ key, value, categoryId }) {
      this.CLEAR_FACET()
      this.setOptionAttributes({ key, value, categoryId })
    },
    setFacetKeyAndSetAttributes({ key, value, categoryId }) {
      this.setFacetKey({
        selectedFacetKey: key,
        selectedFacetCategoryId: this.masterCategoryId
      })
      this.setOptionAttributes({ key, value, categoryId })
    },
    onChangeFocusTab(val) {
      this.focusedTabId = val.id
      this.setFacetKey({
        selectedFacetKey: val.key,
        selectedFacetCategoryId: this.masterCategoryId
      })
    },
    getFocusedTabId() {
      if (this.selectedFacetKey) {
        const selected = this.conditionOptions.find(
          conditionOption => conditionOption.master.key === this.selectedFacetKey
        )
        if (selected) {
          return selected.master.id
        }
      }
      let notSelectedTabId = null
      for (let i = 0; i < this.conditionOptions?.length; i++) {
        if (
          !this.conditionOptions[i].option.value &&
          !this.conditionOptions[i].option.valuesList.length
        ) {
          notSelectedTabId = Number(this.conditionOptions[i].master.id)
          break
        }
      }
      if (notSelectedTabId) {
        return notSelectedTabId
      }
      return this.conditionOptions?.length ? this.conditionOptions[0].master.id : null
    }
  }
}
</script>
<style lang="scss" scoped>
.c-serviceSearchHeaderFacet {
  &_cassettes {
    margin-top: 16px;
  }
  &_selectedItems {
    margin-top: 24px;
  }
}
@media (max-width: breakpoint(Search, M)) {
  .c-serviceSearchHeaderFacet {
    &_selectedItems {
      margin-top: 16px;
    }
  }
}
</style>
